<template>
  <a-alert v-if="hasArrears" type="warning" show-icon class="arrears-alert">
    <template slot="message">
      <span class="arrears-alert__title">
        {{ $t("paymentStatus.pastYearsArrearsTitle") }}
      </span>
      <span class="arrears-alert__total">
        {{ formatAmount(total) }}
      </span>
    </template>

    <template slot="description">
      <div class="arrears-alert__summary">
        {{
          $t("paymentStatus.pastYearsArrearsSummary", {
            fees: feeCount,
            years: yearNames,
          })
        }}
      </div>

      <div class="arrears-alert__actions">
        <a-button size="small" @click="expanded = !expanded">
          <a-icon :type="expanded ? 'up' : 'down'" />
          {{
            expanded
              ? $t("paymentStatus.pastYearsArrearsHide")
              : $t("paymentStatus.pastYearsArrearsDetails")
          }}
        </a-button>
      </div>

      <div v-show="expanded" class="arrears-alert__detail">
        <div
          v-for="year in years"
          :key="year.schoolarYear._id"
          class="arrears-alert__year"
        >
          <div class="arrears-alert__year-head">
            <span class="arrears-alert__year-name">
              {{ year.schoolarYear.name }}
            </span>
            <strong>{{ formatAmount(year.total) }}</strong>
            <a-button
              v-if="canCollect(year)"
              type="primary"
              size="small"
              @click="collect(year)"
            >
              <a-icon type="credit-card" />
              {{
                $t("paymentStatus.pastYearsArrearsCollect", {
                  year: year.schoolarYear.name,
                })
              }}
            </a-button>
          </div>

          <div
            v-for="student in year.students"
            :key="`${year.schoolarYear._id}-${student._id}`"
            class="arrears-alert__student"
          >
            <div class="arrears-alert__student-head">
              <span>{{ student.fullName }}</span>
              <strong>{{ formatAmount(student.total) }}</strong>
            </div>
            <div
              v-for="group in student.groups"
              :key="group.paymentId"
              class="arrears-alert__group"
            >
              <div class="arrears-alert__group-name">
                <a-tag :color="group.typeAS === 'S' ? 'blue' : 'purple'">
                  {{
                    group.typeAS === "S"
                      ? $t("paymentStatus.scolarite")
                      : $t("paymentStatus.activite")
                  }}
                </a-tag>
                {{ groupName(group) }}
              </div>
              <a-tag
                v-for="fee in group.fees"
                :key="`${group.paymentId}-${fee.feeName}`"
                color="orange"
              >
                {{ feeLabel(fee, group) }} · {{ formatAmount(fee.amount) }}
              </a-tag>
            </div>
          </div>
        </div>
      </div>
    </template>
  </a-alert>
</template>

<script>
/* eslint-disable */
import store from "store";
import moment from "moment";
import { mapState } from "vuex";
import { formatFeeName } from "@/utilities/settings";
import { fetchPastYearsArrears } from "@/services/pastYearsArrears";

export default {
  name: "PastYearsArrearsAlert",

  props: {
    // Either a parent (all of their children) or an explicit student list.
    parentId: {
      type: String,
      default: null,
    },
    studentIds: {
      type: Array,
      default: () => [],
    },
    // Hide the "collect on year X" shortcut where switching year would
    // interrupt the user (e.g. mid invoice).
    allowCollect: {
      type: Boolean,
      default: true,
    },
    // Already-fetched payload, when the parent needs it too (e.g. to badge the
    // button that opens this view). Skips the request entirely.
    arrears: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      fetchedArrears: null,
      expanded: false,
      currency: "TND",
    };
  },

  computed: {
    ...mapState(["settings"]),

    arrearsData() {
      return this.arrears || this.fetchedArrears;
    },

    hasArrears() {
      return Boolean(this.arrearsData && this.arrearsData.hasArrears);
    },

    years() {
      return (this.arrearsData && this.arrearsData.years) || [];
    },

    total() {
      return (this.arrearsData && this.arrearsData.total) || 0;
    },

    feeCount() {
      return (this.arrearsData && this.arrearsData.feeCount) || 0;
    },

    yearNames() {
      return this.years.map((year) => year.schoolarYear.name).join(", ");
    },
  },

  watch: {
    parentId() {
      this.fetchArrears();
    },
    studentIds() {
      this.fetchArrears();
    },
  },

  created() {
    this.fetchArrears();
  },

  methods: {
    formatAmount(amount) {
      return `${Number(amount || 0).toFixed(2)} ${this.currency}`;
    },

    groupName(group) {
      if (!group.name) return "--";
      return group.groupName ? `${group.groupName} — ${group.name}` : group.name;
    },

    feeLabel(fee, group) {
      const label = formatFeeName(fee.feeName, group.daily, this.$t.bind(this));
      // Arrears span several school years, so months carry their year to keep
      // e.g. June 2025 and June 2026 apart.
      const date = moment(fee.feeName, moment.ISO_8601, true);
      if (!date.isValid() || group.daily) return label;
      return `${label} ${date.format("YYYY")}`;
    },

    canCollect(year) {
      if (!this.allowCollect) return false;
      // The year must be selectable, otherwise the topbar would show a blank.
      return (this.settings.schoolarYears || []).some(
        (option) => String(option._id) === String(year.schoolarYear._id)
      );
    },

    async fetchArrears() {
      if (this.arrears) return;

      this.fetchedArrears = await fetchPastYearsArrears({
        parentId: this.parentId,
        studentIds: this.studentIds,
      });
      this.$emit("loaded", this.fetchedArrears);
    },

    collect(year) {
      const yearId = String(year.schoolarYear._id);
      store.set("activeSchoolarYear", yearId);
      this.$store.commit("settings/CHANGE_SETTING", {
        setting: "activeSchoolarYear",
        value: yearId,
      });

      const studentIds = year.students.map((student) => student._id).join(",");
      // The router runs in hash mode, so the target has to live in the hash.
      // A hash change alone never reloads, and every mounted view still holds
      // data from the year we are leaving — hence the explicit reload.
      window.location.hash = `#/new-payment?students=${studentIds}`;
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.arrears-alert {
  margin-bottom: 16px;
}

.arrears-alert__title {
  font-weight: 600;
}

.arrears-alert__total {
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #fff1b8;
  font-weight: 700;
  white-space: nowrap;
}

.arrears-alert__summary {
  color: #595959;
}

.arrears-alert__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.arrears-alert__detail {
  margin-top: 12px;
  border-top: 1px solid #ffe58f;
  padding-top: 8px;
}

.arrears-alert__year + .arrears-alert__year {
  margin-top: 14px;
  border-top: 1px dashed #ffe58f;
  padding-top: 10px;
}

.arrears-alert__year-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.arrears-alert__year-name {
  font-weight: 700;
}

.arrears-alert__student {
  margin-top: 8px;
  padding-left: 8px;
}

.arrears-alert__student-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
}

.arrears-alert__group {
  margin-top: 4px;
  padding-left: 8px;
}

.arrears-alert__group-name {
  color: #8c8c8c;
  font-size: 12px;
  margin-bottom: 2px;
}
</style>
